import { useEffect, useState } from "react";
import {
	appendTripStops,
	type TripWorkspace,
	type TripPlanItem,
} from "@japangolmok/core";
import { curatedWalks, type CuratedWalk } from "@japangolmok/content";
import { savedWalks, toggleSavedWalk } from "../storage/saved-walks";
import { channelShareLink, shareMessage, CopyText } from "./ShareSheet";
import { isAppsInTossChannel } from "../api-url";
import { candidateToStop, type PersistTrip } from "./TripExperience";
import { saveTrip } from "../storage/trips";
export function WalkBookmark({ slug, title }: { slug: string; title: string }) {
	const [saved, setSaved] = useState(false),
		[error, setError] = useState("");
	useEffect(() => {
		savedWalks()
			.then((rows) => setSaved(rows.some((w) => w.slug === slug)))
			.catch(() => setError("찜 목록을 읽지 못했어요."));
	}, [slug]);
	return (
		<>
			<button
				type="button"
				className="button secondary"
				aria-pressed={saved}
				onClick={async () => {
					try {
						await toggleSavedWalk(slug);
						setSaved(!saved);
					} catch {
						setError("찜을 저장하지 못했어요. 다시 눌러 주세요.");
					}
				}}
			>
				{saved ? "찜 해제" : "찜"}
			</button>
			<button
				type="button"
				className="button secondary"
				onClick={async () => {
					try {
						const link = isAppsInTossChannel()
							? await window.__japangolmokCreateShareLink?.(
									`intoss://japangolmok/walks/${slug}`,
								)
							: `https://japangolmok.com/walks/${slug}`;
						if (!link) throw new Error("SHARE_UNAVAILABLE");
						await shareMessage(`${title} · 여기 어때?\n${link}`);
					} catch {
						setError(
							isAppsInTossChannel()
								? "토스 공유를 열지 못했어요. 코스 이름을 복사해 주세요."
								: "공유창을 열지 못했어요. 주소를 복사해 주세요.",
						);
					}
				}}
			>
				여기 어때? 보내기
			</button>
			<CopyText
				text={
					isAppsInTossChannel()
						? title
						: `${title}\nhttps://japangolmok.com/walks/${slug}`
				}
				label="코스 글 복사"
			/>
			{error && <p role="alert">{error}</p>}
		</>
	);
}
export function SavedWalkSuggestions({
	trip,
	persist,
}: {
	trip: TripWorkspace;
	persist: PersistTrip;
}) {
	const [rows, setRows] = useState<CuratedWalk[]>([]),
		[notice, setNotice] = useState("");
	useEffect(() => {
		savedWalks()
			.then(setRows)
			.catch(() => setNotice("찜한 코스를 읽지 못했어요."));
	}, []);
	const matches = rows.filter((w) =>
		w.airportCodes.includes(trip.destination as never),
	);
	return (
		<>
			{matches.length > 0 && (
				<aside>
					<h3>찜한 코스 {matches.length}개 담기</h3>
					<p>{matches.map((w) => w.title).join(" · ")}</p>
					<button
						type="button"
						className="button secondary"
						onClick={async () => {
							if (
								(await persist((t) => {
									matches.forEach((w, i) => {
										const day = t.days[Math.min(i, t.days.length - 1)]!;
										Object.assign(
											t,
											appendTripStops(
												t,
												day.date,
												w.stops.map((p) => candidateToStop(p, w.id)),
											),
										);
									});
								})) !== false
							)
								setNotice(
									"찜한 코스를 담았어요. 같은 날의 중복 장소는 유지했어요.",
								);
						}}
					>
						찜한 코스 담기
					</button>
				</aside>
			)}
			<p role="status">{notice}</p>
		</>
	);
}
export function TravelCollections({
	trips,
	onChanged,
}: {
	trips: TripWorkspace[];
	onChanged: () => Promise<void>;
}) {
	const [selected, setSelected] = useState<string[]>([]),
		[target, setTarget] = useState(""),
		[notice, setNotice] = useState("");
	const cities = new Map<string, Set<string>>();
	trips.forEach((t) => {
		for (const item of t.days
			.flatMap((d) => d.items)
			.filter((i) => i.completed)) {
			const set = cities.get(t.destinationCity) ?? new Set<string>();
			set.add(item.placeId ?? item.title);
			cities.set(t.destinationCity, set);
		}
	});
	const returns = trips
		.flatMap((t) =>
			[...t.days.flatMap((d) => d.items), ...(t.candidates ?? [])]
				.filter((i) => i.returnReason || !i.completed)
				.map((i) => ({ ...i, sourceTrip: t.id, destination: t.destination })),
		)
		.filter(
			(p, i, a) =>
				a.findIndex(
					(x) => (x.placeId ?? x.title) === (p.placeId ?? p.title),
				) === i,
		);
	return (
		<section className="travel-collections">
			{cities.size > 0 && (
				<>
					<h2>내 일본 지도 · {cities.size}개 도시</h2>
					<ul>
						{[...cities].map(([city, places]) => (
							<li key={city}>
								{city} · 다녀온 장소 {places.size}곳
							</li>
						))}
					</ul>
				</>
			)}
			<details className="workspace-disclosure">
				<summary>다시 갈 곳 모아 담기</summary>
				{returns.length ? (
					returns.map((p, i) => (
						<label key={`${p.sourceTrip}:${p.id}`}>
							<input
								type="checkbox"
								checked={selected.includes(p.id)}
								onChange={(e) =>
									setSelected(
										e.target.checked
											? [...selected, p.id]
											: selected.filter((id) => id !== p.id),
									)
								}
							/>
							{p.title}
							{p.returnReason && ` · ${p.returnReason}`}
						</label>
					))
				) : (
					<p>
						아직 다시 갈 곳이 없어요. 여행의 장소에 다시 갈 이유를 남겨 보세요.
					</p>
				)}
				<label>
					담을 여행
					<select value={target} onChange={(e) => setTarget(e.target.value)}>
						<option value="">여행 고르기</option>
						{trips.map((t) => (
							<option key={t.id} value={t.id}>
								{t.title}
							</option>
						))}
					</select>
				</label>
				<button
					type="button"
					className="button secondary"
					disabled={!selected.length || !target}
					onClick={async () => {
						const chosen = trips.find((t) => t.id === target);
						if (!chosen) return;
						try {
							const copy = structuredClone(chosen);
							const items = returns
								.filter((p) => selected.includes(p.id))
								.map(({ sourceTrip, destination, ...p }) => ({
									...p,
									id: crypto.randomUUID(),
									completed: false,
									time: null,
									locked: false,
								}));
							copy.candidates = [...(copy.candidates ?? []), ...items];
							await saveTrip(copy);
							setNotice("고른 장소를 여행 후보로 담았어요.");
							await onChanged();
						} catch {
							setNotice(
								"담지 못했어요. 원래 여행은 그대로예요. 저장 공간을 확인해 주세요.",
							);
						}
					}}
				>
					고른 곳을 여행 후보로 담기
				</button>
				<button
					type="button"
					className="button secondary"
					disabled={!selected.length}
					onClick={() => {
						try {
							const items = returns
								.filter((p) => selected.includes(p.id))
								.map(({ sourceTrip, destination, ...p }) => p);
							sessionStorage.setItem("jgm-return-draft", JSON.stringify(items));
							window.location.assign(
								`/trips/new?returnDraft=1&destination=${returns.find((p) => selected.includes(p.id))?.destination ?? "FUK"}`,
							);
						} catch {
							setNotice(
								"후보를 이어가지 못했어요. 기존 여행에 담기를 이용해 주세요.",
							);
						}
					}}
				>
					새 여행 만들고 담기
				</button>
			</details>
			<p role="status">{notice}</p>
		</section>
	);
}
